import { z } from "zod";
import { dietSchema } from "./enums";

/**
 * Ordering: orders, batches, order items, KOTs and service requests. See
 * docs/architecture/ordering-billing-gst.md and data-model.md, Ordering.
 *
 * Orders snapshot names, prices and tax rates at the time of sale, so
 * editing the menu never changes a past order or bill.
 */

/** One chosen add-on option as it was when ordered: `order_items.modifiers`. */
export const orderItemModifierSchema = z.object({
	/** The `modifier_options` row, for reports. */
	id: z.uuid(),
	groupId: z.uuid(),
	name: z.string(),
	/** For one unit. */
	priceDeltaPaise: z.number().int(),
});
export type OrderItemModifier = z.infer<typeof orderItemModifierSchema>;

/** One line of a KOT as printed: `kots.lines`. */
export const kotLineSchema = z.object({
	orderItemId: z.uuid(),
	/** The print name when the item has one. */
	name: z.string(),
	variantName: z.string().nullable(),
	/** Option names, printed indented. */
	modifiers: z.array(z.string()),
	quantity: z.number().int().min(1),
	note: z.string().nullable(),
	diet: dietSchema,
});
export type KotLine = z.infer<typeof kotLineSchema>;

// ---------------------------------------------------------------------------
// Guest sessions. See storefront-sites.md, Guest session contract.
// ---------------------------------------------------------------------------

/** `public.ordering.startGuestSession`: the token of the scanned QR code. */
export const startGuestSessionInputSchema = z.object({ qrToken: z.string().max(64) });

/**
 * The session for `/t/<qrToken>`. The storefront server sets `token` as the
 * `lm_guest` cookie with `guestCookieHeader` (realtime.ts) and redirects to
 * `/order`.
 */
export const guestSessionStartSchema = z.object({
	token: z.string(),
	expiresAt: z.iso.datetime(),
	maxAgeSeconds: z.number().int(),
	restaurantId: z.uuid(),
	table: z.object({ id: z.uuid(), label: z.string(), areaName: z.string().nullable() }),
});
export type GuestSessionStart = z.infer<typeof guestSessionStartSchema>;
